import { type ReactNode, useState } from 'react';
import { CommandRoot, CommandEmpty, CommandInput, CommandList } from '@discover-ui/command';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@discover-ui/dialog';
import Button from '@discover-ui/button';
import Chip from '@discover-ui/chip';
import CloseIcon from '@discover-ui/icons/close.svg';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import CR257ChipOptionList, { type CR257ChipOptionGroup } from './CR257ChipOptionList';

type SelectedOption = { id: number; label: string };

type CR257ChipMultiSelectModalProps = {
  children: ReactNode;
  groups: CR257ChipOptionGroup[];
  isAtMax: boolean;
  maxSelections: number;
  onOpenChange: (open: boolean) => void;
  onToggle: (id: number) => void;
  open: boolean;
  selectedIds: Set<number>;
  selectedOptions: SelectedOption[];
};

export default function CR257ChipMultiSelectModal({
  children,
  groups,
  isAtMax,
  maxSelections,
  onOpenChange,
  onToggle,
  open,
  selectedIds,
  selectedOptions,
}: CR257ChipMultiSelectModalProps) {
  const [search, setSearch] = useState('');
  const { track } = useAnalytics();

  const closeModal = () => {
    setSearch('');
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true);
      return;
    }

    track('suburb_selector_dismissed', {
      suburbs_selected_count: selectedIds.size,
    });
    closeModal();
  };

  const handleSave = () => {
    track('suburb_selector_save_clicked', {
      suburbs_selected_count: selectedIds.size,
    });
    closeModal();
  };

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      {children}
      <DialogContent
        overlayClassName="z-[1000]"
        closeButtonClassName="right-3 top-3"
        className="bottom-auto left-1/2 top-1/2 z-[1000] max-h-[90vh] min-h-[90vh] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 gap-4 overflow-hidden rounded-xl p-4 supports-[height:100dvh]:max-h-[90dvh] supports-[height:100dvh]:min-h-[90dvh]">
        <DialogHeader className="my-0 shrink-0 pr-8 text-left">
          <DialogTitle className="text-xl font-semibold">Select suburbs</DialogTitle>
          <DialogDescription className="sr-only">
            Search and select up to {maxSelections} suburbs
          </DialogDescription>
        </DialogHeader>

        <CommandRoot className="min-h-0 flex-1 overflow-hidden bg-transparent">
          <CommandInput
            clearable
            value={search}
            onValueChange={setSearch}
            placeholder="Search suburbs"
            className="border border-input bg-background focus:border-ds-brand-500 focus-visible:border-ds-brand-500"
            rootClassName="shrink-0 px-0"
          />

          <p className="mb-0 mt-3 shrink-0 text-sm text-muted-foreground">
            Selected location {selectedOptions.length}/{maxSelections}
          </p>

          {selectedOptions.length > 0 && (
            <div className="my-2 flex shrink-0 flex-wrap gap-2">
              {selectedOptions.map(({ id, label }) => (
                <Chip
                  key={id}
                  className="rounded-full bg-ds-brand-950 px-3 py-1.5 text-sm font-medium text-white">
                  <span className="max-w-40 truncate">{label}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    className="inline-flex items-center justify-center rounded-full p-0.5 text-white transition-opacity hover:opacity-80"
                    onClick={() => onToggle(id)}>
                    <CloseIcon className="size-3" />
                  </button>
                </Chip>
              ))}
            </div>
          )}

          <CommandList className="mt-3 max-h-none min-h-0 flex-1 px-0 pb-1">
            <CommandEmpty>No locations found.</CommandEmpty>
            <CR257ChipOptionList
              groups={groups}
              selectedIds={selectedIds}
              isAtMax={isAtMax}
              onToggle={onToggle}
            />
          </CommandList>
        </CommandRoot>

        <Button
          type="button"
          width="wide"
          disabled={selectedIds.size === 0}
          className="shrink-0 disabled:bg-ds-gray-light-25 disabled:text-ds-gray-light-400 disabled:opacity-100"
          onClick={handleSave}>
          Save
        </Button>
      </DialogContent>
    </DialogRoot>
  );
}
